import React from 'react';

import {
  Box,
  Card as MuiCard,
  CardContent,
  LinearProgress,
  Stack,
  styled,
  Typography
} from '@mui/material';
import _ from 'lodash';
import {
  baseTypes,
  routeDirectory
} from 'modules';
import { useNavigate } from 'react-router-dom';

import * as FundraisingProgressLabel from './components/FundraisingProgressLabel';
import * as RaisedMoneyInfo from './components/RaisedMoneyInfo';



const StyledCardContent = styled(CardContent)<{variant?: 'detail' | 'summary'}>(({ variant, theme }) => ({
  cursor: variant === 'summary' ? 'pointer' : undefined,
  backgroundColor: variant === 'detail' ? '#fff' : '#F0F7FA',
  ':hover': {
    backgroundColor: '#FFFFFF'
  },
  transition: 'ease-in-out 0.2s',
  padding: theme.spacing(3)
}));

const StyledLinearProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'cardVariant' && prop !== 'isComplete'
})<{
  cardVariant?: IProps['variant'];
  isComplete: boolean;
}>(({ isComplete, cardVariant }) => ({
  borderRadius: cardVariant === 'detail' ? 4 : 2,
  height: cardVariant === 'detail' ? 8 : 4,
  minWidth:  372,
  '& .MuiLinearProgress-barColorPrimary': {
    backgroundColor: isComplete ? '#6FCF97' : undefined
  }
}));

const SummaryBox = styled(Box)<{variant?: 'detail' | 'summary'}>(({ variant, theme }) => ({
  display: 'flex',
  flexDirection: variant === 'detail' ? 'column-reverse' : 'row',
  alignItems: 'flex-start',
  justifyContent: variant === 'summary' ? 'space-between' : 'flex-start',
  marginBottom: variant === 'detail' ? theme.spacing(2) : 0
}));

const StageNameText = styled(Typography)(({ theme }) => ({
  color: '#87acb9',
  marginBottom: theme.spacing(1)
}));

const FinancialInfoBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between'
});

const Card = styled(MuiCard)({
  maxWidth: 420
});

const TitleTypography = styled(Typography, {
  shouldForwardProp: prop => prop !== 'cardVariant'
})<{cardVariant?: IProps['variant']}>(({ cardVariant }) => ({
  fontSize: cardVariant === 'detail' ? 34 : 20
}));



interface IProps {
  variant?: 'detail' | 'summary';
  campaignSummary: baseTypes.IFundraisingCampaignSummary
}

export const FC: React.FC<IProps> = ({ variant, campaignSummary }) => {
  const navigate = useNavigate();
  const percentageFill = React.useMemo(() =>
    _.clamp(campaignSummary.amountRaisedDollars / (campaignSummary.targetAmountDollars || 1) * 100, 0, 100),
  [campaignSummary]);

  return (
    <Card
      key={campaignSummary.id}
      elevation={variant === 'detail' ? 0 : undefined}
      onClick={() => navigate(routeDirectory.fundraise.path(campaignSummary.id))}>
      <StyledCardContent variant={variant}>

        <Stack spacing={2}>
          <SummaryBox variant={variant}>
            <TitleTypography
              variant="h3"
              cardVariant={variant}>
              {campaignSummary.name}
            </TitleTypography>

            <StageNameText>
              {campaignSummary.stageDisplayName}
            </StageNameText>
          </SummaryBox>

          <StyledLinearProgress
            cardVariant={variant}
            isComplete={percentageFill >= 100}
            value={percentageFill}
            variant="determinate" />

          <FinancialInfoBox>
            <RaisedMoneyInfo.FC
              total={campaignSummary.targetAmountDollars}
              raised={campaignSummary.amountRaisedDollars} />

            <FundraisingProgressLabel.FC completedAt={campaignSummary.completedAt} />
          </FinancialInfoBox>
        </Stack>
      </StyledCardContent>
    </Card>
  );
};
